// Receipt.js - Fixed version to resolve all errors
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import NumberToWords from 'number-to-words';
import FeemonkLogo from '../../../Assets/images/FeeMonk_HighRes_Logo.png'
import iacgLogo from "../../../Assets/images/iacglogo.png"
import apollologo from "../../../Assets/images/apollo.png"
import joyLogo from "../../../Assets/images/joy.png"
import ushodayaLogo from "../../../Assets/images/Ushodaya.png"
import indicLogo from "../../../Assets/images/Indic.jpg"

// Create styles using StyleSheet.create for better performance
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  header: {
    marginBottom: 10,
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    width: 80,
    height: 'auto',
    marginBottom: 5,
  },
  companyName: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 5,
    textAlign: 'center',
  },
  address: {
    fontSize: 10,
    textAlign: 'center',
    marginBottom: 5,
  },
  receiptTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 10,
    marginTop: 2,
  },
  headerFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  divider: {
    marginTop: 10,
    marginBottom: 10,
    height: 1,
    backgroundColor: 'black',
  },
  transactionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 10,
    borderBottom: '1px dashed #000',
    marginBottom: 15,
  },
  boldText: {
    fontFamily: 'Helvetica-Bold',
  },
  detailRow: {
    marginBottom: 5,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 105,
    paddingRight: 105,
  },
  footer: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  footerText: {
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
});

const Receipt = ({ company, user, receiptNo, logo }) => {
  const currentDate = new Date().toLocaleDateString();
  const currentTime = new Date().toLocaleTimeString();
  
  // Comprehensive validation
  if (!user || !company) {
    console.error('Missing required props for Receipt component');
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Error: Missing receipt data</Text>
        </Page>
      </Document>
    );
  }

  // Safe conversion with proper error handling
  let amountInWords = 'Zero';
  try {
    if (user.amount && typeof user.amount === 'number' && user.amount > 0) {
      amountInWords = NumberToWords.toWords(user.amount);
    }
  } catch (error) {
    console.error('Error converting amount to words:', error);
    amountInWords = 'Amount conversion error';
  }
  
  // Logo selection with proper validation
  const getLogoSrc = () => {
    const logoMap = {
      "iacg": iacgLogo,
      "ushodaya": ushodayaLogo,
      "joyinternational": joyLogo,
      "indic": indicLogo,
      "apollomedskills": apollologo,
    };
    
    return logoMap[logo] || FeemonkLogo;
  };

  const getAddress = () => {
    const addressMap = {
      "iacg": company.iacgAddress,
      "ushodaya": company.ushodayaAddress,
      "joyinternational": company.joyAddress,
      "indic": company.indicAddress,
      "apollomedskills": company.apolloAddress,
    };
    
    return addressMap[logo] || company.address || 'Address not available';
  };

  // Safe string conversion for all text values
  const safeString = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    return String(value);
  };

  // Safe number formatting
  const formatAmount = (amount) => {
    try {
      const numAmount = Number(amount) || 0;
      return numAmount.toLocaleString('en-US', { minimumFractionDigits: 2 });
    } catch (error) {
      return '0.00';
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          {/* Logo with proper error handling */}
          <Image 
            src={getLogoSrc()} 
            style={styles.logo}
          />
          
          <Text style={styles.companyName}>
            {safeString(company.name, 'Company Name')}
          </Text>
          
          <Text style={styles.address}>
            {safeString(getAddress())}
          </Text>
          
          <Text style={styles.receiptTitle}>
            General Receipt
          </Text>
          
          <View style={styles.headerFooter}>
            <Text>Printed on: {currentDate} {currentTime}</Text>
            <Text>                                            </Text>
            <Text style={styles.boldText}>Hyderabad</Text>
          </View>
        </View>
        
        {/* Divider */}
        <View style={styles.divider} />

        {/* Body */}
        <View style={{ marginBottom: 10 }}>
          {/* Transaction Header */}
          <View style={styles.transactionHeader}>
            <Text style={styles.boldText}>
              Transaction Id: {safeString(user?.LoanID)}
            </Text>
            <Text>Date of Payment: {safeString(user.dateOfPayment)}</Text>
          </View>
          
          {/* Fee Cycle ID */}
          <View style={styles.detailRow}>
            <Text>Fee Cycle ID</Text> 
            <Text style={styles.boldText}>{safeString(user.loanType)}</Text>
          </View>
          
          {/* Received from */}
          <View style={styles.detailRow}>
            <Text>Received with thanks from</Text> 
            <Text>{safeString(user.username)}</Text>
          </View>
          
          {/* Amount */}
          <View style={styles.detailRow}>
            <Text>Amount</Text> 
            <Text>{formatAmount(user.amount)}</Text>
          </View>
          
          {/* Amount in words */}
          <View style={styles.detailRow}>
            <Text>Amount in words</Text>
            <Text style={styles.boldText}>{safeString(amountInWords)} rupees only</Text>
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            ***This is a system generated receipt***
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default Receipt;